import { useEffect, useState } from "react";

/**
 * A React hook that listens to CSS media query changes and returns whether the query matches.
 *
 * This hook provides a reactive way to respond to media query changes, such as viewport size,
 * orientation, or any other media features supported by the CSS @media rule.
 *
 * @param query - A valid CSS media query string (e.g., "(min-width: 440px)", "(prefers-color-scheme: dark)")
 * @returns boolean - True if the media query matches, false otherwise
 *
 * @example
 * // Check if viewport is desktop size
 * const isDesktop = useMediaQuery("(min-width: 440px)");
 *
 * // Check if user prefers dark mode
 * const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
 *
 */
export const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    setMatches(media.matches);

    const listener = (e: MediaQueryListEvent) => setMatches(e.matches);
    media.addEventListener("change", listener);

    return () => media.removeEventListener("change", listener);
  }, [query]);

  return matches;
};
